/**
 * The signed-in member's current access token, for the consent bridge.
 *
 * The bridge is the one surface that has to present the member's own credential
 * to a host other than the API: the MCP server verifies it against Supabase and
 * resolves the organisation from it. Everywhere else the token stays inside the
 * auth module, and it still does here — the reader is supplied by the auth root,
 * which owns the Supabase client, and merely threaded down.
 *
 * This module used to construct a second Supabase client over the same storage.
 * It worked, but it put two owners on one session for no benefit, so the token
 * now arrives as a prop and the fallback below returns nothing rather than
 * opening a client of its own.
 */
import { shouldBypassAuthenticationForE2e } from "../auth";

export type AccessTokenReader = () => Promise<string | null>;

/**
 * The fixture token the end-to-end suite runs on.
 *
 * The e2e build replaces the whole authentication stack with a bypass
 * (`shouldBypassAuthenticationForE2e` — dev builds, `e2e` mode, explicit opt-in
 * flag), so no auth root mounts and no session exists to read. Returning a
 * fixture keeps the bridge exercisable there under exactly the conditions that
 * bypass already establishes; a production build cannot reach this line, because
 * `import.meta.env.DEV` is false.
 */
const E2E_FIXTURE_TOKEN = "e2e-consent-bridge-session";

/**
 * The reader used when the auth root did not supply one. Outside the e2e bypass
 * that means no session is available, and the bridge says so rather than
 * guessing — a consent screen that cannot name the organisation must not offer
 * to approve anything.
 */
export function createAccessTokenReader(
  environment: ImportMetaEnv = import.meta.env,
): AccessTokenReader {
  if (shouldBypassAuthenticationForE2e(environment)) {
    return () => Promise.resolve(E2E_FIXTURE_TOKEN);
  }
  return () => Promise.resolve(null);
}
